<!-- eslint-disable vue/valid-template-root -->
<template lang="pug"></template>

<script setup lang="ts">
import { nanoid } from 'nanoid';
import { LngLatBounds } from 'mapbox-gl';

import type { LngLatLike, GeoJSONSource } from 'mapbox-gl';
import type { FcwMapProvide } from '@/components/molecules/FcwMap.vue';

export interface FcwMapLineProps {
  /**
   * The coordinates of the line
   */
  coordinates?: LngLatLike[] | null;
  /**
   * Color of the line
   */
  color?: Color;
  /**
   * Whether the line is dashed
   */
  dashed?: boolean;
  /**
   * Width of the line
   */
  width?: number;
  /**
   * Fit bounds padding display option
   */
  fitBoundsPadding?: number;
  /**
   * Fit the bounds when the component mounts. Default to false
   */
  fitBoundsOnMount?: boolean;
  /**
   * Fit bounds when coordinates changes
   */
  autoFitBounds?: boolean;
  /**
   * Id of the line layer
   */
  layerId?: string;
  /**
   * Max zoom level of the line
   */
  maxZoom?: number;
  /**
   * Min zoom level of the line
   */
  minZoom?: number;
  /**
   * Visibility of the line
   */
  visible?: boolean;
}

const props = withDefaults(defineProps<FcwMapLineProps>(), {
  modelValue: false,
  coordinates: () => [],
  contentClass: '',
  color: 'primary',
  dashed: false,
  width: 3,
  layerId: nanoid(),
  fitBoundsPadding: 32,
  fitBoundsOnMount: false,
  autoFitBounds: true,
  withAnimation: false,
  maxZoom: 22,
  minZoom: 0,
  visible: true,
});

const { map, isMapLoaded } = inject<FcwMapProvide>('map') ?? {
  map: null,
  isMapLoaded: ref(false),
};

const lineColor = computed(() => {
  const style = getComputedStyle(document.body);
  return style.getPropertyValue(getCssColor(props.color, true));
});

const sourceId = nanoid();

watch(isMapLoaded, isLoaded => {
  if (!isLoaded) return;

  map?.value?.addSource(sourceId, {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: props.coordinates as Coordinates[],
      },
    },
  });

  map?.value?.addLayer({
    id: props.layerId,
    type: 'line',
    source: sourceId,
    paint: {
      'line-color': lineColor.value,
      'line-width': props.width,
      'line-dasharray': props.dashed ? [6, 4] : [],
    },
    maxzoom: props.maxZoom,
    minzoom: props.minZoom,
  });

  map?.value?.on('resize', () => {
    props.autoFitBounds && fitLineBounds();
  });

  props.fitBoundsOnMount && fitLineBounds();
});

watch(
  () => props.coordinates,
  () => {
    (map?.value?.getSource(sourceId) as GeoJSONSource)?.setData({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: props.coordinates as Coordinates[],
      },
    });
    props.autoFitBounds && fitLineBounds();
  }
);

watch(
  () => props.width,
  () => {
    map?.value?.setPaintProperty(props.layerId, 'line-width', props.width);
  }
);

watchImmediate(
  () => props.visible,
  isVisible => {
    map?.value?.setLayoutProperty(
      props.layerId,
      'visibility',
      isVisible ? 'visible' : 'none'
    );
  }
);

watch(lineColor, () => {
  map?.value?.setPaintProperty(props.layerId, 'line-color', lineColor.value);
});

defineExpose<{
  fitBounds: () => void;
}>({
  fitBounds: fitLineBounds,
});

/**
 * Fit the map view to the line
 * @param coordinates - Array of coordinates
 * @param options - Mapbox fitbounds options
 */
function fitLineBounds(): void {
  if (!map?.value || !props.coordinates?.length) return;

  map?.value.fitBounds(
    props.coordinates.reduce(
      (bounds, coord) => bounds.extend(coord),
      new LngLatBounds(props.coordinates[0], props.coordinates[0])
    ),
    {
      padding: props.fitBoundsPadding,
    }
  );
}
</script>
